/**
 * Gesetzliche Feiertage Österreich — bundesweit, ohne Landes-Varianten.
 *
 * DIESER KALENDER LEBT NUR IM FRONTEND, und das ist die eigentliche Aussage:
 * Er INFORMIERT einen Menschen, er RECHNET nichts. Es gab am 13.08.2026 eine
 * Kopie im Worker, die aus dem Kalender einen EFZG-Tag ableitete — sie buchte
 * Benjamin Atzler einen Feiertag, an dem er nachweislich auf der Baustelle war
 * (Export 152 = 8 statt 7). Die Kopie ist gelöscht. Damit kann aus diesem
 * Kalender strukturell kein Lohnwert mehr entstehen: der Wert kommt
 * ausschließlich aus dem Feld, das Franka füllt.
 *
 * Franka am 13.08.2026 auf unsere Rückfrage nach abweichenden Landesregelungen:
 * *"Als Feiertage sind bitte einfach die gesetzlichen Feiertage für Österreich
 * zu hinterlegen."* Damit gibt es genau EINE Liste und keine Bundesland-Dimension.
 *
 * Thomas am 13.08. zur Bedienung: *"wechsel der feiertage nur durch uns dann
 * prüfen wir den impact. self service ist hier unnötige komplexität ... wir
 * löschen diese komplexität."* Deshalb steht der Kalender hier im Code und nicht
 * in einer Einstellungsmaske: Ein Feiertagskalender ändert sich pro Land
 * praktisch nie, ist für alle Mandanten desselben Landes identisch, und eine
 * falsche Auswahl hätte Lohnwirkung.
 *
 * NICHT enthalten, bewusst:
 *  - Karfreitag — seit 2019 kein allgemeiner gesetzlicher Feiertag mehr
 *    (persönlicher Feiertag nach § 7a ARG ist etwas anderes und steht dem
 *    Dienstnehmer frei; er ist nicht aus dem Kalender ableitbar).
 *  - 24.12. und 31.12. — arbeitsfrei nach Kollektivvertrag oder Usance, aber
 *    keine gesetzlichen Feiertage.
 */

/** Ostersonntag nach Meeus/Jones/Butcher (gregorianisch). */
export function ostersonntag(jahr: number): { monat: number; tag: number } {
  const a = jahr % 19;
  const b = Math.floor(jahr / 100);
  const c = jahr % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const monat = Math.floor((h + l - 7 * m + 114) / 31);
  const tag = ((h + l - 7 * m + 114) % 31) + 1;
  return { monat, tag };
}

function alsUtc(jahr: number, monat: number, tag: number): number {
  return Date.UTC(jahr, monat - 1, tag);
}

const TAG_MS = 86_400_000;

export interface Feiertag {
  /** ISO-Datum, z. B. "2026-06-04". */
  datum: string;
  name: string;
}

function iso(ms: number): string {
  const d = new Date(ms);
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${mm}-${dd}`;
}

/** Alle gesetzlichen Feiertage eines Jahres, aufsteigend nach Datum. */
export function feiertageAt(jahr: number): Feiertag[] {
  const o = ostersonntag(jahr);
  const ostern = alsUtc(jahr, o.monat, o.tag);
  const beweglich: Array<[number, string]> = [
    [ostern + 1 * TAG_MS, "Ostermontag"],
    [ostern + 39 * TAG_MS, "Christi Himmelfahrt"],
    [ostern + 50 * TAG_MS, "Pfingstmontag"],
    [ostern + 60 * TAG_MS, "Fronleichnam"],
  ];
  const fest: Array<[number, number, string]> = [
    [1, 1, "Neujahr"],
    [1, 6, "Heilige Drei Könige"],
    [5, 1, "Staatsfeiertag"],
    [8, 15, "Mariä Himmelfahrt"],
    [10, 26, "Nationalfeiertag"],
    [11, 1, "Allerheiligen"],
    [12, 8, "Mariä Empfängnis"],
    [12, 25, "Christtag"],
    [12, 26, "Stefanitag"],
  ];
  const alle: Feiertag[] = [
    ...fest.map(([m, t, name]) => ({ datum: iso(alsUtc(jahr, m, t)), name })),
    ...beweglich.map(([ms, name]) => ({ datum: iso(ms), name })),
  ];
  alle.sort((a, b) => a.datum.localeCompare(b.datum));
  return alle;
}

/** Wochentag eines ISO-Datums, ausgeschrieben. */
const WOCHENTAG = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

/**
 * Der HINWEIS ueber dem Feld „Feiertage (EFZG)": alle gesetzlichen Feiertage des
 * Monats, mit Wochentag — BEWUSST OHNE Mo-Fr-Filter.
 *
 * Begruendung (Karte 720, 13.08.2026): Ein Mo-Fr-Filter waere eine Annahme ueber
 * Arbeitstage, die bei 3G nicht belegbar ist — 0 von 94 Dienstnehmern haben einen
 * `work_schedule` (beim Bestandsmandanten 41 von 44). Sobald der MENSCH die Zahl
 * eintraegt, ist gefilterte Information schlechter als vollstaendige. Deshalb
 * liefert diese Funktion alles und ueberlaesst die Wahl Franka.
 *
 * `monat` im Format "YYYY-MM" (das Format der Karte). Leeres Array, wenn der
 * Monat keinen Feiertag hat oder das Format nicht stimmt.
 */
export function feiertagsHinweis(monat: string | null | undefined): Array<{ datum: string; name: string; wochentag: string; tag: number }> {
  const m = /^(\d{4})-(\d{2})$/.exec(monat ?? "");
  if (!m) return [];
  const jahr = Number(m[1]);
  const mon = Number(m[2]);
  if (!Number.isFinite(jahr) || mon < 1 || mon > 12) return [];
  return feiertageAt(jahr)
    .filter((f) => Number(f.datum.slice(5, 7)) === mon)
    .map((f) => {
      const [jj, mm, tt] = f.datum.split("-").map(Number);
      const d = new Date(Date.UTC(jj!, mm! - 1, tt!));
      return { datum: f.datum, name: f.name, wochentag: WOCHENTAG[d.getUTCDay()]!, tag: tt! };
    });
}
